import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Users, Heart, MessageCircle, UserPlus, Send, Trophy, Dumbbell, Share2, Globe, Swords, Crown, UserMinus, X, ImagePlus, Loader2, Check, CheckCheck } from "lucide-react";
import { useWebSocket } from "../context/WebSocketContext";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import Challenges from "../components/Challenges";
import CommunityChallenges from "../components/CommunityChallenges";
import { useBuddyInvites } from "../context/BuddyInvitesContext";
import { compressImage } from "../lib/imageCompress";

export default function Social() {
  const { user } = useAuth();
  const { refresh: refreshInvites } = useBuddyInvites();
  const { subscribe } = useWebSocket();
  const [tab, setTab] = useState("feed");
  const [feed, setFeed] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [buddies, setBuddies] = useState({ accepted: [], incoming: [], outgoing: [] });
  const [inviteEmail, setInviteEmail] = useState("");
  const [chatWith, setChatWith] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [postText, setPostText] = useState("");
  // Pending image attachment for the next chat message (data URL).
  const [pendingImage, setPendingImage] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const [sending, setSending] = useState(false);
  // Whether the other party is currently typing. Auto-clears after 4s of
  // no follow-up `chat:typing` events to avoid stuck indicators.
  const [otherTyping, setOtherTyping] = useState(false);
  const typingClearTimerRef = useRef(null);
  // Tracks when WE last sent a typing ping so we can throttle to ~once / 3s.
  const lastTypingSentRef = useRef(0);

  const loadAll = async () => {
    const [f, b, d] = await Promise.all([api.get("/feed"), api.get("/buddies"), api.get("/feed/discover")]);
    setFeed(f.data); setBuddies(b.data); setDiscover(d.data);
  };
  useEffect(() => { loadAll(); }, []);

  // Live feed updates: every new public post fans out a `feed:new` WS event
  // to every connected user. Prepend optimistically so users never need to
  // refresh the Squad tab. Also re-fetch on tab focus / visibilitychange so
  // PWAs that suspended the WS connection still re-sync quickly.
  // `feed:remove` cleans up deleted posts (e.g. when a user hides their own
  // goal-hit auto-post) so timelines stay accurate without a reload.
  useEffect(() => {
    const unsubFeed = subscribe("feed:new", (msg) => {
      if (!msg?.post) return;
      setFeed((prev) => (prev.some((p) => p.post_id === msg.post.post_id) ? prev : [msg.post, ...prev]));
      setDiscover((prev) => (prev.some((p) => p.post_id === msg.post.post_id) ? prev : [msg.post, ...prev]));
    });
    const unsubRemove = subscribe("feed:remove", (msg) => {
      if (!msg?.post_id) return;
      setFeed((prev) => prev.filter((p) => p.post_id !== msg.post_id));
      setDiscover((prev) => prev.filter((p) => p.post_id !== msg.post_id));
    });
    const refetchOnFocus = () => { if (document.visibilityState === "visible") loadAll(); };
    document.addEventListener("visibilitychange", refetchOnFocus);
    window.addEventListener("focus", refetchOnFocus);
    return () => {
      unsubFeed && unsubFeed();
      unsubRemove && unsubRemove();
      document.removeEventListener("visibilitychange", refetchOnFocus);
      window.removeEventListener("focus", refetchOnFocus);
    };
  }, [subscribe]);

  // Live chat: append incoming messages for the thread we're currently viewing.
  // Messages for other threads still arrive but we silently increment a
  // notification badge later (Phase 2). For now we just toast the sender.
  useEffect(() => {
    const unsub = subscribe("chat:message", (msg) => {
      if (chatWith && (msg.from_user_id === chatWith.id || msg.to_user_id === chatWith.id)) {
        // Skip if it's a message we already appended optimistically.
        setMessages((prev) => prev.some((m) => m.message_id === msg.message_id) ? prev : [...prev, msg]);
        // If the incoming message is FROM the person we're chatting with,
        // we're already looking at it — mark it read immediately so their
        // ✓ → ✓✓ flip is instant. Fire-and-forget; we don't await.
        if (msg.from_user_id === chatWith.id) {
          api.post(`/chat/${chatWith.id}/read`).catch(() => {});
        }
      } else if (msg.from_user_id !== user.user_id) {
        toast(`💬 ${msg.from_name}: ${(msg.text || "📷 Photo").slice(0, 60)}`, {
          description: "Open Buddies tab to reply",
        });
      }
    });
    // Server tells us the recipient just read some of our outgoing messages.
    // Patch the local list so check marks update without a refetch.
    const unsubRead = subscribe("chat:read", (payload) => {
      const ids = new Set(payload.message_ids || []);
      if (ids.size === 0) return;
      setMessages((prev) => prev.map((m) => ids.has(m.message_id) ? { ...m, read_at: payload.read_at } : m));
    });
    // Other party started typing in this thread.
    const unsubTyping = subscribe("chat:typing", (payload) => {
      if (!chatWith || payload.from_user_id !== chatWith.id) return;
      setOtherTyping(true);
      if (typingClearTimerRef.current) clearTimeout(typingClearTimerRef.current);
      typingClearTimerRef.current = setTimeout(() => setOtherTyping(false), 4000);
    });
    // Buddy events refresh the buddies list so accepted/outgoing stay live.
    const unsubAcc = subscribe("buddy:accepted", () => { loadAll(); refreshInvites(); });
    const unsubReq = subscribe("buddy:request", () => { loadAll(); });
    return () => { unsub(); unsubRead(); unsubTyping(); unsubAcc(); unsubReq(); };
  }, [subscribe, chatWith, user.user_id, refreshInvites]);

  const sendRequest = async (e) => {
    e.preventDefault();
    const target = (inviteEmail || "").trim().toLowerCase();
    if (!target) return;
    try {
      await api.post("/buddies/request", { email: target });
      toast.success(`Invite sent to ${target}`);
      setInviteEmail("");
      loadAll();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 404) {
        // Most common cause: recipient hasn't signed up yet. Surface that clearly
        // instead of a vague "User not found" so the user knows what to do next.
        toast.error(`No account found for ${target}`, {
          description: "Ask them to sign up at gymwithgrizz.app first, then re-invite.",
          duration: 7000,
        });
      } else if (status === 400 && /already/i.test(detail || "")) {
        toast.error("You've already invited this user (or they've invited you).");
      } else {
        toast.error(detail || "Couldn't send invite — try again");
      }
    }
  };

  const accept = async (rid) => { await api.post("/buddies/accept", { request_id: rid }); loadAll(); refreshInvites(); };
  const remove = async (rid) => { await api.delete(`/buddies/${rid}`); loadAll(); refreshInvites(); };

  const removeBuddy = async (b) => {
    const name = b.from_user_id === user.user_id ? b.to_name : b.from_name;
    if (!window.confirm(`Remove ${name} from your gym buddies? You can re-invite them later.`)) return;
    try {
      await remove(b.request_id);
      toast.success(`Removed ${name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't remove");
    }
  };

  const cancelOutgoing = async (b) => {
    try {
      await remove(b.request_id);
      toast(`Cancelled request to ${b.to_name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't cancel");
    }
  };

  const openChat = async (b) => {
    const other = b.from_user_id === user.user_id ? { id: b.to_user_id, name: b.to_name } : { id: b.from_user_id, name: b.from_name };
    setChatWith(other);
    setPendingImage(null);
    setOtherTyping(false);
    const { data } = await api.get(`/chat/${other.id}`);
    setMessages(data);
    // Mark every incoming message as read (fires `chat:read` WS to sender).
    api.post(`/chat/${other.id}/read`).catch(() => {});
  };
  const sendMessage = async () => {
    if (!chatWith) return;
    const text = msgText.trim();
    if (!text && !pendingImage) return;
    setSending(true);
    try {
      const { data } = await api.post("/chat/send", { to_user_id: chatWith.id, text, image: pendingImage });
      // Optimistic local push — WS event will also fire for our other tabs.
      setMessages((m) => m.some((x) => x.message_id === data.message_id) ? m : [...m, data]);
      setMsgText("");
      setPendingImage(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSending(false); }
  };
  // Throttled outbound typing ping — fires at most once every 3s while the
  // user is actively typing. Cheap fire-and-forget HTTP call; the backend
  // fans out a transient `chat:typing` WS event to the recipient.
  const pingTyping = () => {
    if (!chatWith) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 3000) return;
    lastTypingSentRef.current = now;
    api.post(`/chat/${chatWith.id}/typing`).catch(() => {});
  };

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-pick the same file later
    if (!file) return;
    setImageBusy(true);
    try {
      const { dataUrl, bytes } = await compressImage(file, { maxWidth: 1024, quality: 0.75 });
      if (bytes > 500_000) {
        // Re-compress harder if the first pass came out big.
        const second = await compressImage(file, { maxWidth: 800, quality: 0.65 });
        setPendingImage(second.dataUrl);
      } else {
        setPendingImage(dataUrl);
      }
    } catch (err) {
      toast.error("Couldn't read that image");
    } finally { setImageBusy(false); }
  };

  const toggleLike = async (post_id) => { await api.post(`/feed/${post_id}/like`); loadAll(); };

  // Pending image attachment for the next feed post (data URL).
  const [postImage, setPostImage] = useState(null);
  const [postImageBusy, setPostImageBusy] = useState(false);

  const handlePostImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPostImageBusy(true);
    try {
      const { dataUrl, bytes } = await compressImage(file, { maxWidth: 1024, quality: 0.75 });
      if (bytes > 500_000) {
        const second = await compressImage(file, { maxWidth: 800, quality: 0.65 });
        setPostImage(second.dataUrl);
      } else {
        setPostImage(dataUrl);
      }
    } catch {
      toast.error("Couldn't read that image");
    } finally { setPostImageBusy(false); }
  };

  const createPost = async () => {
    const text = postText.trim();
    if (!text && !postImage) return;
    try {
      await api.post("/feed", { text, kind: "note", is_public: true, image: postImage });
      setPostText("");
      setPostImage(null);
      loadAll();
      toast.success("Posted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't post");
    }
  };

  const sharePost = async (post) => {
    const u = await api.get("/auth/me");
    const gymTag = u.data.gym_name ? ` @ ${u.data.gym_name}` : "";
    const buddyNames = buddies.accepted.map((b) => b.from_user_id === user.user_id ? b.to_name : b.from_name);
    const buddyTag = buddyNames.length ? ` w/ ${buddyNames.join(", ")}` : "";
    const text = `${post.user_name}: ${post.text}${gymTag}${buddyTag}`;
    const url = window.location.origin;
    if (navigator.share) navigator.share({ title: "GYM with GRIZZ", text, url }).catch(() => {});
    else { navigator.clipboard.writeText(text + " " + url); toast.success("Copied — paste to FB/IG"); }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6">
      <h1 className="font-display text-4xl sm:text-5xl uppercase leading-none mb-1">Squad</h1>
      <div className="text-xs tracking-[0.2em] uppercase text-neutral-400 mb-5">Buddies · Discover · Challenges</div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none h-auto p-0 flex-wrap">
          <TabsTrigger value="feed" data-testid="social-tab-feed" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Users className="w-3 h-3 mr-1.5" /> Squad</TabsTrigger>
          <TabsTrigger value="discover" data-testid="social-tab-discover" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Globe className="w-3 h-3 mr-1.5" /> Discover</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="social-tab-challenges" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white"><Swords className="w-3 h-3 mr-1.5" /> Challenges</TabsTrigger>
          <TabsTrigger value="buddies" data-testid="social-tab-buddies" className="rounded-none h-10 px-4 font-display uppercase tracking-wider data-[state=active]:bg-blue-500 data-[state=active]:text-white">Buddies</TabsTrigger>
        </TabsList>

        <TabsContent value="feed" className="mt-5">
          <div className="border border-neutral-800 p-3 mb-4">
            <Input data-testid="post-input" placeholder="Share with your squad..." value={postText} onChange={(e) => setPostText(e.target.value)} className="bg-neutral-900 border-neutral-800 text-white rounded-none" />
            {postImage && (
              <div className="flex items-center gap-2 mt-2 p-2 border border-blue-500/40 bg-blue-500/5">
                <img src={postImage} alt="preview" className="w-12 h-12 object-cover" />
                <div className="text-xs text-neutral-300 flex-1">Ready to post</div>
                <Button size="icon" variant="ghost" data-testid="post-image-clear" onClick={() => setPostImage(null)} className="h-7 w-7 text-neutral-400 hover:text-blue-500">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            )}
            <div className="flex justify-between items-center mt-2">
              <input
                id="post-image-input"
                type="file"
                accept="image/*"
                data-testid="post-image-input"
                onChange={handlePostImagePick}
                className="hidden"
              />
              <Button
                size="sm"
                data-testid="post-image-btn"
                variant="ghost"
                onClick={() => document.getElementById("post-image-input")?.click()}
                disabled={postImageBusy}
                title="Attach photo"
                className="text-neutral-400 hover:text-blue-500 uppercase tracking-wider font-display h-8"
              >
                {postImageBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><ImagePlus className="w-3 h-3 mr-1" /> Photo</>}
              </Button>
              <Button data-testid="post-btn" onClick={createPost} size="sm" disabled={!postText.trim() && !postImage} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">Post</Button>
            </div>
          </div>
          <PostList posts={feed} userId={user.user_id} onLike={toggleLike} onShare={sharePost} reload={loadAll} />
        </TabsContent>

        <TabsContent value="discover" className="mt-5">
          <CommunityChallenges />
          <h3 className="font-display text-lg uppercase mb-2 mt-6">Community Posts</h3>
          <PostList posts={discover} userId={user.user_id} onLike={toggleLike} onShare={sharePost} reload={loadAll} />
        </TabsContent>

        <TabsContent value="challenges" className="mt-5">
          <Challenges />
        </TabsContent>

        <TabsContent value="buddies" className="mt-5">
          <form onSubmit={sendRequest} className="flex gap-2 mb-5">
            <Input data-testid="buddy-email-input" placeholder="Buddy's email" type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="bg-neutral-900 border-neutral-800 text-white rounded-none h-11" />
            <Button data-testid="buddy-invite-btn" type="submit" className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11">
              <UserPlus className="w-4 h-4 mr-1" /> Invite
            </Button>
          </form>

          {buddies.incoming.length > 0 && (
            <div className="mb-5">
              <h3 className="font-display text-lg uppercase mb-2">Incoming</h3>
              <div className="divide-y divide-neutral-800 border border-neutral-800">
                {buddies.incoming.map((b) => (
                  <div key={b.request_id} className="p-3 flex items-center justify-between">
                    <div className="font-semibold">{b.from_name}</div>
                    <div className="flex gap-2">
                      <Button size="sm" data-testid={`accept-${b.request_id}`} onClick={() => accept(b.request_id)} className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8">Accept</Button>
                      <Button size="sm" variant="outline" onClick={() => remove(b.request_id)} className="bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider h-8">Deny</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <h3 className="font-display text-lg uppercase mb-2">Buddies ({buddies.accepted.length})</h3>
          {buddies.accepted.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-6 text-center text-sm text-neutral-400">No buddies yet.</div>
          ) : (
            <div className="divide-y divide-neutral-800 border border-neutral-800">
              {buddies.accepted.map((b) => {
                const other = b.from_user_id === user.user_id ? b.to_name : b.from_name;
                return (
                  <div key={b.request_id} className="p-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 bg-blue-500 flex items-center justify-center font-display text-sm shrink-0">{other?.[0]?.toUpperCase()}</div>
                      <div className="font-semibold truncate">{other}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button size="sm" data-testid={`chat-${b.request_id}`} onClick={() => openChat(b)} className="bg-transparent border border-neutral-700 text-white hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-8">
                        <MessageCircle className="w-3 h-3 mr-1" /> Chat
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`remove-buddy-${b.request_id}`}
                        onClick={() => removeBuddy(b)}
                        title={`Remove ${other}`}
                        className="text-neutral-400 hover:text-blue-500 h-8 w-8"
                      >
                        <UserMinus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {buddies.outgoing.length > 0 && (
            <div className="mt-5">
              <h3 className="font-display text-lg uppercase mb-2">Outgoing</h3>
              <div className="divide-y divide-neutral-800 border border-neutral-800">
                {buddies.outgoing.map((b) => (
                  <div key={b.request_id} className="p-3 flex items-center justify-between gap-2 text-sm">
                    <span className="truncate">{b.to_name}</span>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-neutral-500 uppercase text-xs tracking-wider">Pending</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        data-testid={`cancel-outgoing-${b.request_id}`}
                        onClick={() => cancelOutgoing(b)}
                        title={`Cancel request to ${b.to_name}`}
                        className="text-neutral-400 hover:text-blue-500 h-7 w-7"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!chatWith} onOpenChange={(o) => { if (!o) { setChatWith(null); setOtherTyping(false); if (typingClearTimerRef.current) { clearTimeout(typingClearTimerRef.current); typingClearTimerRef.current = null; } } }}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-md h-[70vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{chatWith?.name}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 py-2">
            {messages.map((m) => {
              const mine = m.from_user_id === user.user_id;
              return (
                <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[78%] px-3 py-2 text-sm ${mine ? "bg-blue-500" : "bg-neutral-800"}`}>
                    {m.image && (
                      <img
                        src={m.image}
                        alt="attachment"
                        className="block max-w-full max-h-72 mb-1 cursor-pointer"
                        onClick={() => window.open(m.image, "_blank")}
                      />
                    )}
                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    {mine && (
                      <div
                        data-testid={`chat-receipt-${m.message_id}`}
                        data-read={m.read_at ? "true" : "false"}
                        className={`flex justify-end mt-0.5 ${m.read_at ? "text-white" : "text-white/60"}`}
                        title={m.read_at ? `Read ${new Date(m.read_at).toLocaleTimeString()}` : "Sent"}
                      >
                        {m.read_at ? <CheckCheck className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {messages.length === 0 && <div className="text-center text-sm text-neutral-500">Say hi 👋</div>}
            {otherTyping && (
              <div data-testid="chat-typing-indicator" className="flex justify-start">
                <div className="bg-neutral-800 px-3 py-2 text-sm flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="inline-block w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="inline-block w-1.5 h-1.5 bg-neutral-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  <span className="ml-1 text-xs text-neutral-400">typing…</span>
                </div>
              </div>
            )}
          </div>

          {pendingImage && (
            <div data-testid="chat-image-preview" className="flex items-center gap-2 p-2 border border-blue-500/40 bg-blue-500/5">
              <img src={pendingImage} alt="preview" className="w-12 h-12 object-cover" />
              <div className="text-xs text-neutral-300 flex-1">Ready to send</div>
              <Button size="icon" variant="ghost" data-testid="chat-image-clear" onClick={() => setPendingImage(null)} className="h-7 w-7 text-neutral-400 hover:text-blue-500">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}

          <div className="flex gap-2 mt-2">
            <input
              id="chat-image-input"
              type="file"
              accept="image/*"
              data-testid="chat-image-input"
              onChange={handleImagePick}
              className="hidden"
            />
            <Button
              size="icon"
              data-testid="chat-image-btn"
              variant="ghost"
              onClick={() => document.getElementById("chat-image-input")?.click()}
              disabled={imageBusy}
              title="Attach photo"
              className="text-neutral-400 hover:text-blue-500 shrink-0 h-10 w-10"
            >
              {imageBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            </Button>
            <Input
              data-testid="chat-input"
              placeholder="Message..."
              value={msgText}
              onChange={(e) => { setMsgText(e.target.value); pingTyping(); }}
              onKeyDown={(e) => e.key === "Enter" && !sending && sendMessage()}
              className="bg-neutral-900 border-neutral-800 text-white rounded-none"
            />
            <Button
              data-testid="chat-send"
              onClick={sendMessage}
              disabled={sending || (!msgText.trim() && !pendingImage)}
              className="bg-blue-500 hover:bg-blue-600 rounded-none"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PostList({ posts, userId, onLike, onShare, reload }) {
  if (posts.length === 0) {
    return (
      <div className="border border-dashed border-neutral-800 p-8 text-center text-sm text-neutral-400">
        <Users className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
        Nothing here yet.
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {posts.map((p) => (
        <PostCard key={p.post_id} post={p} userId={userId} onLike={onLike} onShare={onShare} reload={reload} />
      ))}
    </div>
  );
}

function PostCard({ post, userId, onLike, onShare, reload }) {
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");

  const loadComments = async () => {
    const { data } = await api.get(`/feed/${post.post_id}/comments`);
    setComments(data);
  };
  const toggle = async () => {
    if (!showComments) await loadComments();
    setShowComments(!showComments);
  };
  const submit = async () => {
    if (!text.trim()) return;
    await api.post(`/feed/${post.post_id}/comment`, { text });
    setText("");
    await loadComments();
  };

  return (
    <div data-testid={`feed-${post.post_id}`} className="border border-neutral-800 p-4">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-9 h-9 bg-blue-500 flex items-center justify-center font-display text-sm">{post.user_name?.[0]?.toUpperCase()}</div>
        <div>
          <div className="font-semibold text-sm">{post.user_name}</div>
          <div className="text-xs text-neutral-500">{new Date(post.created_at).toLocaleString()}</div>
        </div>
        {post.kind === "pr" && <Trophy className="ml-auto w-4 h-4 text-blue-500" />}
        {post.kind === "workout" && <Dumbbell className="ml-auto w-4 h-4 text-blue-500" />}
        {post.kind === "challenge" && <Swords className="ml-auto w-4 h-4 text-blue-500" />}
      </div>
      <div className="text-white mb-3 whitespace-pre-wrap">{post.text}</div>
      {post.image && (
        <img
          src={post.image}
          alt="post"
          className="block w-full max-h-96 object-contain mb-3 cursor-pointer bg-neutral-900"
          onClick={() => window.open(post.image, "_blank")}
        />
      )}
      <div className="flex gap-4 text-sm">
        <button data-testid={`like-${post.post_id}`} onClick={() => onLike(post.post_id)} className={`flex items-center gap-1 ${post.likes?.includes(userId) ? "text-blue-500" : "text-neutral-400"} hover:text-blue-500`}>
          <Heart className="w-4 h-4" fill={post.likes?.includes(userId) ? "currentColor" : "none"} /> {post.likes?.length || 0}
        </button>
        <button data-testid={`comments-${post.post_id}`} onClick={toggle} className="flex items-center gap-1 text-neutral-400 hover:text-blue-500">
          <MessageCircle className="w-4 h-4" /> {comments.length || ""}
        </button>
        <button data-testid={`share-${post.post_id}`} onClick={() => onShare(post)} className="flex items-center gap-1 text-neutral-400 hover:text-blue-500">
          <Share2 className="w-4 h-4" />
        </button>
      </div>
      {showComments && (
        <div className="mt-3 pt-3 border-t border-neutral-800 space-y-2">
          {comments.map((c) => (
            <div key={c.comment_id} className="text-sm">
              <span className="font-semibold text-blue-500">{c.user_name}: </span>
              <span className="text-neutral-200">{c.text}</span>
            </div>
          ))}
          {comments.length === 0 && <div className="text-xs text-neutral-500">Be the first to comment.</div>}
          <div className="flex gap-2 pt-2">
            <Input data-testid={`comment-input-${post.post_id}`} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Add a comment..." className="bg-neutral-900 border-neutral-800 text-white rounded-none h-9 text-sm" />
            <Button data-testid={`comment-submit-${post.post_id}`} size="sm" onClick={submit} className="bg-blue-500 hover:bg-blue-600 rounded-none h-9">Send</Button>
          </div>
        </div>
      )}
    </div>
  );
}
